import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'
import { isAuthError } from './error-utils'
import { getRuntimeEnv } from './runtime-env'

/**
 * Initializes Sentry only when a DSN is injected at runtime (window.__ENV__ via getRuntimeEnv). With
 * no DSN — local dev, or any deploy where error reporting is disabled and the SENTRY_DSN secret
 * resolves empty — this is a no-op, so the SPA sends no events at all. Guarding on the injected DSN
 * (rather than a hardcoded literal) lets a customer's "Error reporting" toggle actually silence the
 * frontend. See ISS-8230.
 */
export function initSentry(): void {
  const { sentryDsn } = getRuntimeEnv()
  if (!sentryDsn) {
    return
  }

  Sentry.init({
    dsn: sentryDsn,
    // eslint-disable-next-line no-restricted-properties -- outside React tree (Sentry init)
    environment: window.location.host,
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/\/api\//],
    beforeSend: (event, hint) => {
      if (isAuthError(hint.originalException)) {
        return null
      }
      return event
    },
  })
}
